import React, { useEffect, useState } from 'react';
import { Route, Link } from 'react-router-dom';
import LogoutButton from './LogoutButton';
import placeholderPhoto from '../assets/new.png';

// Floating bar in the bottom-left corner of every page:
// - Back / Forward buttons (browser history) on every page, and a "Home" button
//   on every page except the home page itself
//   (needed mainly for the installed app (PWA), which has no browser address bar)
// - when logged in: the member's email + balance, a "Your profile" button back to
//   their own bio page, and a Logout button next to Home

const barStyle = {
  position: 'fixed',
  left: 'calc(16px + env(safe-area-inset-left))',
  bottom: 'calc(16px + env(safe-area-inset-bottom))',
  zIndex: 2147483646,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  maxWidth: 'calc(100vw - 32px)'
};

const pillStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 16px',
  borderRadius: '999px',
  background: 'rgba(17, 24, 39, 0.85)',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: 1,
  textDecoration: 'none',
  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
  WebkitTapHighlightColor: 'transparent'
};

const arrowStyle = {
  ...pillStyle,
  margin: 0,
  border: 'none',
  padding: '10px 14px',
  fontFamily: 'inherit',
  cursor: 'pointer'
};

const logoutStyle = {
  ...pillStyle,
  margin: 0,
  border: 'none',
  background: 'rgba(198, 40, 40, 0.9)',
  fontFamily: 'inherit',
  cursor: 'pointer'
};

const userStyle = {
  padding: '6px 12px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.95)',
  color: '#111827',
  fontSize: '13px',
  lineHeight: 1.4,
  textAlign: 'left',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%'
};

const avatarStyle = {
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  objectFit: 'cover',
  objectPosition: 'top',
  flexShrink: 0,
  display: 'block'
};

// Facebook-style grey "no photo" person, used while the member is still on the placeholder photo
const NoPhotoAvatar = () => (
  <svg viewBox="0 0 40 40" style={avatarStyle} aria-hidden="true">
    <circle cx="20" cy="20" r="20" fill="#c9ccd1" />
    <circle cx="20" cy="15" r="7" fill="#fff" />
    <path d="M6 34c2-7.5 7.5-11 14-11s12 3.5 14 11a19.9 19.9 0 0 1-28 0z" fill="#fff" />
  </svg>
);

// Size in bytes of a file, without downloading it when the server gives Content-Length
async function fileSize(url) {
  const head = await fetch(url, { method: 'HEAD', cache: 'no-cache' });
  if (!head.ok) return null;
  const length = head.headers.get('content-length');
  if (length) return Number(length);
  const res = await fetch(url, { cache: 'no-cache' });
  return res.ok ? (await res.blob()).size : null;
}

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

function FloatingBar({ pathname, history }) {
  const [email, setEmail] = useState(() => readStorage('email'));
  const [balance, setBalance] = useState(() => readStorage('balance'));
  const [bioUrl, setBioUrl] = useState(() => readStorage('bio_url'));
  // URL of the member's own photo, or null while they are on the placeholder / unknown
  const [photoUrl, setPhotoUrl] = useState(null);

  // Re-check login on every page change (picks up login/logout),
  // and refresh the balance from the server.
  useEffect(() => {
    const savedEmail = readStorage('email');
    setEmail(savedEmail);
    setBalance(readStorage('balance'));
    const savedBioUrl = (readStorage('bio_url') || '').replace(/\/+$/, '');
    setBioUrl(savedBioUrl || null);
    if (!savedEmail) {
      setPhotoUrl(null);
      return undefined;
    }

    let cancelled = false;

    // Show the member's photo on the profile button, unless it is still the placeholder.
    // Placeholder photos are byte-identical copies of assets/new.png, so compare sizes.
    const slug = savedBioUrl.startsWith('/bio') ? savedBioUrl.slice(4) : '';
    if (/^[a-z0-9]+$/.test(slug)) {
      const url = `/bio-photo/${slug}`;
      Promise.all([fileSize(url), fileSize(placeholderPhoto)])
        .then(([size, placeholderSize]) => {
          if (cancelled) return;
          setPhotoUrl(size && size !== placeholderSize ? `${url}?v=${size}` : null);
        })
        .catch(() => {});
    } else {
      setPhotoUrl(null);
    }

    fetch(`/user-details?email=${encodeURIComponent(savedEmail)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !data || data.balance === undefined) return;
        setBalance(String(data.balance));
        try {
          localStorage.setItem('balance', String(data.balance));
        } catch (err) {
          // storage unavailable: just show it
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname]);

  const showHome = pathname !== '/';

  return (
    <React.Fragment>
      {/* Spacer so the last bit of each page can scroll out from under the bar */}
      <div aria-hidden="true" style={{ height: `calc(${email ? 175 : 72}px + env(safe-area-inset-bottom))` }} />
      <div style={barStyle}>
        {email && (
          <div style={userStyle}>
            <div><strong>Email:</strong> {email}</div>
            <div><strong>Balance:</strong> {balance !== null && balance !== 'undefined' ? balance : '-'}</div>
          </div>
        )}
        {email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {bioUrl && (
              <Link to={bioUrl} style={pillStyle} aria-label="Go to your profile (bio page)">
                {photoUrl
                  ? <img src={photoUrl} alt="" style={avatarStyle} onError={() => setPhotoUrl(null)} />
                  : <NoPhotoAvatar />}
                <span>Your profile</span>
              </Link>
            )}
            <LogoutButton buttonStyle={logoutStyle} />
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" style={arrowStyle} onClick={() => history.goBack()} aria-label="Go back to the previous page">
            <span aria-hidden="true">◀</span>
          </button>
          {showHome && (
            <Link to="/" style={pillStyle} aria-label="Go to home page">
              <span aria-hidden="true">🏠</span>
              <span>Home</span>
            </Link>
          )}
          <button type="button" style={arrowStyle} onClick={() => history.goForward()} aria-label="Go forward to the next page">
            <span aria-hidden="true">▶</span>
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

const HomeButton = () => (
  <Route render={({ location, history }) => <FloatingBar pathname={location.pathname} history={history} />} />
);

export default HomeButton;
